import React, { useEffect, useMemo, useState } from "react"
import { get } from "lodash"
import uuid from "uuid"
import { useDispatch, useSelector } from "react-redux"

import {
  FourValue,
  FourHalfValue,
  SignValue,
  Portfolio,
  Register,
} from "components/organisms/panel/register"
import { Panel } from "components/organisms/panel"
import MiddleChart from "components/molecules/chart/middleChart"
import { InfoTable } from "components/molecules/table"
import { infoTableColumns } from "config/table_columns"
import { RegisterPropsType } from "components/pages/main/register"
import { changeSymbol } from "redux/ducks/Chart"
import { APIResponse } from "redux/apis/types"
import { MainContainer } from "components/molecules/utils/mainContainer"
import store from "redux/store"
import { changeRegisterSheet } from "redux/ducks/RegisterSymbol"
import { NewsList } from "components/organisms/panel/newsList"
import { DataDescription } from "components/organisms/panel/dataDescription"
import {
  RegisterSymbolDisclaimerText,
  PortfolioDisclaimerText,
} from "config/disclaimerText"
import { checkIsSp } from "utils/util"
import { LoadingType } from "../../../redux/ducks/AutoLoadingFlag"

type useSetChartSymbolDataType = (data: APIResponse.Res, id: string) => void
const useSetChartSymbolData: useSetChartSymbolDataType = (data, id) => {
  const dispatch = useDispatch()
  useEffect(() => {
    if (!data) return
    dispatch(changeSymbol(id, get(data, "Symbol")))
  }, [data])
}

export const FourTemplate: React.FC<RegisterPropsType> = props => {
  const isSP = useMemo(() => {
    return checkIsSp()
  }, [])
  const canvasId = useMemo(() => {
    return uuid()
  }, [])
  useSetChartSymbolData(props.symbolData, canvasId)

  const sheetNO = get(props.registerSymbol, "symbolSheet.SheetNo")
  useEffect(() => {
    if (isFinite(sheetNO)) {
      store.dispatch(changeRegisterSheet(String(sheetNO)))
    }
  }, [])

  return (
    <MainContainer className="right-thin-layout-register fifty-symbol">
      <div className="layout-register">
        <div className="contents-block four-list-block">
          <FourValue {...props} />
        </div>
        {!isSP && (
          <div className="contents-block side-symbol-block">
            <div className="contents-nest-block">
              <Panel
                className="panel-main-index"
                title=""
                nonePanelHeader={true}
              >
                <MiddleChart id={canvasId} />
                <InfoTable
                  list={infoTableColumns.price}
                  data={get(props.symbolData, "Symbol", [])}
                  className="table-striped table-index-detail"
                  wrapperCls="index-detail-table-row"
                />
              </Panel>
              {!props.registerWindow &&
                get(props.symbolData, "Symbol.Symbol") && (
                  <NewsList symbol={get(props.symbolData, "Symbol.Symbol")} />
                )}
            </div>
          </div>
        )}
      </div>
      {!isSP && (
        <DataDescription
          className="panel-data-description"
          children={RegisterSymbolDisclaimerText}
        />
      )}
    </MainContainer>
  )
}

export const FourHalfTemplate: React.FC<RegisterPropsType> = props => {
  const [firstRenderFlag, setFirstRenderFlag] = useState(false)
  const isSP = checkIsSp()
  const canvasId = useMemo(() => {
    return uuid()
  }, [])
  useSetChartSymbolData(props.symbolData, canvasId)

  const autoLoadingFlag = useSelector(
    (state: { AutoLoadingFlag: LoadingType }) =>
      state.AutoLoadingFlag.autoLoading
  )

  const sheetNO = get(props.registerSymbol, "symbolSheet.SheetNo")
  useEffect(() => {
    if (isFinite(sheetNO)) {
      store.dispatch(changeRegisterSheet(String(sheetNO)))
    }
    setFirstRenderFlag(true)
  }, [])

  useEffect(() => {
    if (firstRenderFlag) store.dispatch(changeRegisterSheet(sheetNO))
  }, [autoLoadingFlag])

  return (
    <MainContainer className="right-thin-layout-register fifty-symbol">
      <div className="layout-register">
        <div className="contents-block">
          <FourHalfValue {...props} />
        </div>
        <div className="contents-block side-symbol-block">
          <div className="contents-nest-block">
            <Panel className="panel-main-index" title="" nonePanelHeader={true}>
              <MiddleChart id={canvasId} />
              <InfoTable
                list={infoTableColumns.price}
                data={get(props.symbolData, "Symbol", [])}
                className="table-striped table-index-detail"
                wrapperCls="index-detail-table-row"
              />
            </Panel>
          </div>
        </div>
      </div>
      {!isSP && (
        <DataDescription
          className="panel-data-description"
          children={RegisterSymbolDisclaimerText}
        />
      )}
    </MainContainer>
  )
}

export const SignTemplate: React.FC<RegisterPropsType> = props => {
  const isSP = checkIsSp()
  const canvasId = useMemo(() => {
    return uuid()
  }, [])
  useSetChartSymbolData(props.symbolData, canvasId)

  const sheetNO = get(props.registerSymbol, "symbolSheet.SheetNo")

  useEffect(() => {
    if (isFinite(sheetNO)) {
      store.dispatch(changeRegisterSheet(String(sheetNO)))
    }
  }, [])

  return (
    <MainContainer className="right-thin-layout-register">
      <div className="layout-register">
        <div className="contents-block sign-list-block">
          <SignValue {...props} />
        </div>
        <div className="contents-block side-symbol-block">
          <div className="contents-nest-block">
            <Panel className="panel-main-index" title="" nonePanelHeader={true}>
              <MiddleChart id={canvasId} />
              <InfoTable
                list={infoTableColumns.price}
                data={get(props.symbolData, "Symbol", [])}
                className="table-striped table-index-detail"
                wrapperCls="index-detail-table-row"
              />
            </Panel>
            {get(props.symbolData, "Symbol.Symbol") && (
              <NewsList symbol={get(props.symbolData, "Symbol.Symbol")} />
            )}
          </div>
        </div>
      </div>
      {!isSP && (
        <DataDescription
          className="panel-data-description"
          children={RegisterSymbolDisclaimerText}
        />
      )}
    </MainContainer>
  )
}

export const PortfolioTemplate: React.FC<RegisterPropsType> = props => {
  const isSP = checkIsSp()
  const canvasId = useMemo(() => {
    return uuid()
  }, [])
  useSetChartSymbolData(props.symbolData, canvasId)

  const sheetNO = get(props.registerSymbol, "symbolSheet.SheetNo")

  useEffect(() => {
    if (isFinite(sheetNO)) {
      store.dispatch(changeRegisterSheet(String(sheetNO)))
    }
  }, [])

  return (
    <MainContainer>
      {/*<MainContainer className="right-thin-layout">*/}
      {/*<div className="contents-block">*/}
      <Portfolio {...props} />
      {/*</div>*/}
      {/*<div className="contents-block side-symbol-block">*/}
      {/*  <div className="contents-nest-block">*/}
      {/*    <Panel className="panel-main-index" title="" nonePanelHeader={true}>*/}
      {/*      <MiddleChart id={canvasId} />*/}
      {/*      <InfoTable*/}
      {/*        list={infoTableColumns.price}*/}
      {/*        data={get(props.symbolData, "Symbol", [])}*/}
      {/*        className="table-striped table-index-detail"*/}
      {/*        wrapperCls="index-detail-table-row"*/}
      {/*      />*/}
      {/*    </Panel>*/}
      {/*    <Panel className="panel-news-list" title="関連ニュース">*/}
      {/*      <NewsListContent />*/}
      {/*    </Panel>*/}
      {/*  </div>*/}
      {/*</div>*/}
      {!isSP && (
        <DataDescription
          className="panel-data-description"
          children={PortfolioDisclaimerText}
        />
      )}
    </MainContainer>
  )
}

export const RegisterTemplate: React.FC<RegisterPropsType> = props => {
  const sheetNO = get(props.registerSymbol, "symbolSheet.SheetNo")

  useEffect(() => {
    if (isFinite(sheetNO)) {
      store.dispatch(changeRegisterSheet(String(sheetNO)))
    }
  }, [])

  return (
    <MainContainer className="register-change">
      <div>
        <Register {...props} />
      </div>
    </MainContainer>
  )
}
